import type { JSX } from 'react';
import { createGlobalStyle } from 'styled-components';
import {
  Bio,
  FullBioToggle,
  SpeakerBioItem,
  SpeakerBioItemContainer,
  SpeakerTileText,
} from '../LayoutComponents';

export type SpeakerBioGridItemStyleStrategy = 'event-room' | 'registration-page';

type StyleStrategyOrNone = SpeakerBioGridItemStyleStrategy | 'none';

type SpeakerStylesContext = {
  showBio: boolean;
  hasBio: boolean;
};

// NOTE: These are all GLOBAL styles, so they are not scoped to a component. Be careful!

const DefaultStyles = createGlobalStyle<SpeakerStylesContext>`
  ${SpeakerBioItem} {
    border-radius: ${({ theme }) => theme.spacing.space03};
  }

  ${SpeakerBioItemContainer} {
    background: ${({ theme }) => theme.color.grey100};
    border: 1px solid ${({ theme }) => theme.color.grey400};
    border-radius: ${({ theme }) => theme.spacing.space03};
    color: ${({ theme }) => theme.color.grey800};

    a {
      color: ${({ theme }) => theme.color.grey800};
    }

    a > svg {
      color: ${({ theme }) => theme.color.grey800};
    }
  }

`;

const EventRoomStyles = createGlobalStyle<SpeakerStylesContext>`
  ${SpeakerBioItem} {
    border-radius: calc(var(--event-room-container-radius, var(--event-room-border-radius)) * 1px);
  }

  ${SpeakerBioItemContainer} {
    background: var(--event-room-speaker-tile-background-color);
    border: var(--event-room-speaker-tile-border);
    border-radius: calc(var(--event-room-container-radius, var(--event-room-border-radius)) * 1px);
    color: var(--event-room-speaker-tile-text-color);
    font-family: var(--event-room-secondary-font);

    ${({ hasBio, showBio }) =>
      showBio &&
      hasBio && {
        boxShadow: '0 4px 2px -2px var(--event-room-speaker-tile-shadow-color)',
        zIndex: 101,
      }}

    .bio,
    .speakerTileText {
      color: var(--event-room-speaker-tile-text-color);

      a {
        color: var(--event-room-speaker-tile-text-color);
      }
    }
  }

  ${Bio} {
    color: var(--event-room-speaker-tile-text-color);
  }

  ${SpeakerTileText} {    
    color: var(--event-room-speaker-tile-text-color);

    &.title {
      color: var(--event-room-speaker-tile-title-text-color);
    }

    a {
      color: var(--event-room-speaker-tile-text-color);
    }
  }

  ${FullBioToggle} {
    background: var(--event-room-speaker-tile-background-color);
    color: var(--event-room-speaker-tile-text-color);
  }
`;

const RegistrationPageStyles = createGlobalStyle<SpeakerStylesContext>`
  ${SpeakerBioItem} {
    border-radius: calc(
      var(
          --event-registration-container-border-radius,
          var(--event-registration-border-radius, 8)
        ) *
        1px
    );
  }

  ${SpeakerBioItemContainer} {
    background: var(--event-registration-speaker-tile-background-color);
    border: var(--event-registration-speaker-tile-border);
    border-radius: calc(
      var(
          --event-registration-container-border-radius,
          var(--event-registration-border-radius, 8)
        ) *
        1px
    );
    color: var(--event-registration-speaker-tile-text-color);
    font-family: var(--event-registration-secondary-font);

    ${({ hasBio, showBio }) =>
      showBio &&
      hasBio && {
        boxShadow: '0 4px 2px -2px var(--event-registration-speaker-tile-shadow-color)',
        zIndex: 101,
      }}

    .bio,
    .speakerTileText {
      color: var(--event-registration-speaker-tile-text-color);

      a {
        color: var(--event-registration-speaker-tile-text-color);
      }
    }
  }

  ${Bio} {
    color: var(--event-registration-speaker-tile-text-color);
  }

  ${SpeakerTileText} {
    color: var(--event-registration-speaker-tile-text-color);

    &.title {
      color: var(--event-registration-speaker-tile-title-text-color);
    }

    a {
      color: var(--event-registration-speaker-tile-text-color);
    }
  }

  ${FullBioToggle} {
    background: var(--event-registration-speaker-tile-background-color);
    color: var(--event-registration-speaker-tile-text-color);
  }
`;

type SpeakerBioStyleProps = {
  styleStrategy?: SpeakerBioGridItemStyleStrategy;
  showBio: boolean;
  hasBio: boolean;
};

export const SpeakerBioStyles = ({
  styleStrategy,
  showBio,
  hasBio,
}: SpeakerBioStyleProps): JSX.Element => {
  const styleStrategyOrNone: StyleStrategyOrNone = styleStrategy ?? 'none';

  switch (styleStrategyOrNone) {
    case 'event-room':
      return (
        <EventRoomStyles
          showBio={showBio}
          hasBio={hasBio}
        />
      );
    case 'registration-page':
      return (
        <RegistrationPageStyles
          showBio={showBio}
          hasBio={hasBio}
        />
      );
    case 'none':
    default:
      return (
        <DefaultStyles
          showBio={showBio}
          hasBio={hasBio}
        />
      );
  }
};
